/**
 * Pure helper: project a payment schedule from raw form/import strings.
 * Extracted verbatim from mock.ts `mockProjectedSchedule` (task 0121.04) so that
 * AddDrawer (subtask 05) can call it for a live "paid / remaining" summary without
 * touching mock internals. mock.ts now delegates here.
 *
 * The math mirrors Loan._installment_amounts (non-uniform last installment — task 0080)
 * and process_monthly_payment_data's paid-history allocation (done / delayed_with_child_payments).
 */
import type { AgreementPaymentSlot } from "../types";

/** Advance a UTC date by n calendar months (same day-of-month). */
function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, d.getUTCDate()));
}

/**
 * Build a projected payment schedule from the raw string inputs (form or import row).
 *
 * Returns null when input is insufficient/invalid:
 *   - any of selling_price / monthly_payment / payment_count is missing or zero
 *   - financed amount (selling − down) ≤ 0
 *
 * @param input   Raw string values (exactly as typed in the form / preview row).
 * @returns       Projected payment slots + remaining debt string, or null.
 */
export function projectSchedule(input: {
  selling_price: string;
  down_payment: string;
  monthly_payment: string;
  payment_count: string;
  paid_amount: string;
  first_payment_date: string;
}): { payments: AgreementPaymentSlot[]; remainder: string } | null {
  const selling = Number(input.selling_price);
  const down = Number(input.down_payment || "0");
  const monthly = Number(input.monthly_payment);
  const count = Number(input.payment_count);
  const paid = Number(input.paid_amount || "0");

  if (!selling || !monthly || !count || count < 1) return null;
  const financed = selling - down;
  if (financed <= 0) return null;

  // Non-uniform last installment: base * (count-1) + last absorbs the rounding remainder.
  const base = Math.round(monthly);
  const last = Math.round(financed - base * (count - 1));
  const amounts: number[] =
    last > 0 ? [...Array(count - 1).fill(base), last] : Array(count).fill(base);

  const start = input.first_payment_date ? new Date(input.first_payment_date) : null;
  let remaining = paid;

  const payments: AgreementPaymentSlot[] = amounts.map((amount, i) => {
    const date = start ? addMonths(start, i).toISOString().slice(0, 10) : null;
    let status: string;
    let paidDate: string | null;
    let paidPortion: number;

    if (remaining <= 0) {
      status = "in_advance";
      paidDate = null;
      paidPortion = 0;
    } else if (remaining >= amount) {
      status = "done";
      paidDate = date;
      paidPortion = amount;
      remaining -= amount;
    } else {
      // Capture the partial amount BEFORE zeroing remaining (mirrors backend paid logic).
      status = "delayed_with_child_payments";
      paidDate = null;
      paidPortion = remaining;
      remaining = 0;
    }

    return {
      number: i + 1,
      status,
      amount: String(amount),
      paid: String(paidPortion),
      payment_date: date,
      paid_date: paidDate,
    };
  });

  return { payments, remainder: String(financed - paid) };
}
